<template>
	<fieldset :class="{ 'rated-topics': true, 'topic-validation-error': validationError }">
		<legend>{{ label ? label : 'Select topics and ratings' }}</legend>
		<div v-for="valueRow in valueRows" :key="valueRow.code" class="rt-row">
			<span class="rt-label">{{ valueRow.label }}</span>
			<span v-if="!checkboxMode" class="rt-level">{{ ratingsUsed.find(r => r.id == valueRow.level)?.name ?? valueRow.level }}</span>
			<span class="rt-remove" @click="deleteValueBtn(valueRow.code)"><i class="dx-icon dx-icon-trash"></i></span>
		</div>
		<DxBox :direction="!isTiny ? 'row' : 'col'" class="add-row">
			<DxItem :ratio="!isTiny ? 1 : 0" base-size="auto">
				<DxSelectBox :data-source="topics" label="Click to add..." value-expr="code" :display-expr="topicDisplayExpr" :search-enabled="true" v-model:value="addTopicCode" v-if="!plaintextMode" />
				<DxTextBox v-model:value="addTopicCustom" :label="emailUniqueMode ? 'Click to enter an email address' : 'Click to enter an item...'" v-if="plaintextMode" />
			</DxItem>
			<DxItem :base-size="10" v-if="!checkboxMode" />
			<DxItem :base-size="!isTiny ? 200 : 'auto'" v-if="!checkboxMode">
				<DxSelectBox :data-source="ratingsUsed" label="Ratings" value-expr="id" display-expr="name" v-model:value="addTopicRating" />
			</DxItem>
			<DxItem :base-size="10" v-if="!needsOtherInput" />
			<DxItem :base-size="!isTiny ? 80 : 'auto'" v-if="!needsOtherInput">
				<DxButton text="Add" styling-mode="solid" type="default" @click="addValueBtn" />
			</DxItem>
		</DxBox>
		<DxBox :direction="isTiny ? 'col' : 'row'" v-if="needsOtherInput" class="other-row">
			<DxItem :ratio="!isTiny ? 1 : 0" base-size="auto">
				<DxTextBox v-model:value="addTopicCustom" label="Specify 'other' entry" />
			</DxItem>
			<DxItem :base-size="10" />
			<DxItem :base-size="!isTiny ? 80 : 'auto'">
				<DxButton text="Add" styling-mode="solid" type="default" @click="addValueBtn" />
			</DxItem>
		</DxBox>
		<DxValidator :adapter="validationAdapter" :validation-group="validationGroup">
			<DxCustomRule :validation-callback="validateCallback" message="You must specify at least one topic." />
		</DxValidator>
		<div v-if="validationError" class="topic-validation-error">You must specify at least one topic.</div>
	</fieldset>
</template>

<script setup>
/* eslint-disable */
import { ref, defineProps, defineEmits, toRefs, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { DxBox, DxItem } from 'devextreme-vue/box';
import DxSelectBox from 'devextreme-vue/select-box';
import DxTextBox from 'devextreme-vue/text-box';
import DxButton from 'devextreme-vue/button';
import DxValidator, { DxCustomRule } from 'devextreme-vue/validator';
import notify from 'devextreme/ui/notify';
import { sizes as mediaSizes, subscribe as subscribeMediaQuery, unsubscribe as unsubscribeMediaQuery } from '../utils/media-query';

const isTiny = ref(mediaSizes()['screen-x-small']);
const mediaQueryHandler = () => {
	isTiny.value = mediaSizes()['screen-x-small'];
};
onMounted(() => {
	subscribeMediaQuery(mediaQueryHandler);
});
onUnmounted(() => {
	unsubscribeMediaQuery(mediaQueryHandler);
});

const props = defineProps([
	'initial',					// Initial array data values
	'topics',					// Array of topic objects (with keys code+name), special code 'other' allows custom input
	'ratings',					// Array of rating objects (with keys id+name) - defaults to 1-5 / unimportant to very important
	'label',					// Text label used for title (optional)
	'checkboxMode',				// Boolean, if true, show checkboxes instead of a dropdown (and disable ratings)
	'plaintextMode',			// Boolean, if true, show a plaintext input instead of a dropdown for adding new topics. NB: Not tested for working with non-checkbox mode!!
	'emailUniqueMode',			// Boolean, if true, use a lowercased email address as validator. Only works in plaintext mode.
	'validationGroup',			// Validation group to use, required for validation to work!
]);
const { label, topics, ratings, checkboxMode, plaintextMode, emailUniqueMode } = toRefs(props);
const emits = defineEmits(["valueChanged"]);
const valueRows = ref([]);

const validationError = ref(false);
const validationCallbacks = [];
const validationAdapter = {
	getValue: () => {
		return valueRows.value?.length ?? 0;
	},
	applyValidationResults: (e) => {
		validationError.value = !e.isValid;
	},
	validationRequestsCallbacks: validationCallbacks,
};
const validateCallback = ({value}) => {
	return value > 0;
};
const revalidateCallback = () => {
	validationCallbacks.forEach((func) => {
		func();
	});
};


// Default ratings to use, replaced with ratings prop if provided
const ratingsUsed = ref([
	{ id: '5', name: 'very important' },
	{ id: '4', name: 'important' },
	{ id: '3', name: 'neutral' },
	{ id: '2', name: 'less important' },
	{ id: '1', name: 'unimportant' }
]);

const topicDisplayExpr = (item) => item ? item.name : null;
const needsOtherInput = computed(() => addTopicCode.value == 'other');

const addTopicCode = ref('');
const addTopicRating = ref('');
const addTopicCustom = ref('');
const addValueBtn = () => {
	// In plaintext mode, we just use the 'other' mode all the time.
	if (plaintextMode.value) {
		if (addTopicCustom.value.trim() == '') {
			return notify({ message: "Please specify a value!", position: 'center' }, "error", 2000);
		}
		if (emailUniqueMode.value) {
			// Simple email validation regex and duplicate checker for case uniqueness (absolute uniqueness is already checked)
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(addTopicCustom.value)) {
				return notify({ message: "Please enter a valid email address!", position: 'center' }, "error", 2000);
			}
			if (valueRows.value.some(v => v.code.toLowerCase() === addTopicCustom.value.toLowerCase())) {
				return notify({ message: "This email address has already been added!", position: 'center' }, "error", 2000); 
			}
		}
		addTopicCode.value = 'other';
	}
	
	if (!addTopicCode.value) {
		return notify({ message: "Please select a topic!", position: 'center' }, "error", 2000);
	} else if (!addTopicRating.value && !checkboxMode.value) {
		return notify({ message: "Please select a rating level!", position: 'center' }, "error", 2000);
	}
	
	// Detect custom 'other' topic, check for input, override code for custom to just be the label itself.
	let addedCode = String(addTopicCode.value);
	let addedLabel = '';
	if (addedCode == 'other') {
		if (addTopicCustom.value.trim() == '') {
			return notify({ message: "Please specify the custom / other topic!", position: 'center' }, "error", 2000);
		}
		addedCode = String(addTopicCustom.value.trim());
		addedLabel = addedCode;
	} else {
		addedLabel = topics.value.find(t => t.code == addedCode)?.name ?? addTopicCode.value;
	}
	
	// Add to our value array, or replace if already present
	let existingIdx = valueRows.value.findIndex(v => v.code == addedCode);
	if (existingIdx != -1) {
		valueRows.value[existingIdx].level = addTopicRating.value;
	} else {
		valueRows.value.push({
			code: addedCode,
			label: addedLabel,
			level: checkboxMode.value ? null : addTopicRating.value,
		});
	}

	// I'm not sure why this bug occurs, but just clearing the input values + emitting our value change directly here gives a internal Vue scheduler error (!)
	// We work around this with a nextTick, but I can't figure out why this is necessary. It only happens with 'other' custom topics, not the preset topics!
	revalidateCallback();
	emits('valueChanged', JSON.parse(JSON.stringify(valueRows.value)));
	nextTick(() => {
		addTopicCode.value = ''; addTopicRating.value = ''; addTopicCustom.value = '';
	});
};

const deleteValueBtn = (code) => {
	valueRows.value = valueRows.value.filter(v => v.code != code);
	revalidateCallback();
	emits('valueChanged', JSON.parse(JSON.stringify(valueRows.value)));
};

// Copy over initial values if provided; this is a one-way binding only!
// Also replace ratings used with custom ratings if provided.
onMounted(() => {
	if (ratings.value && Array.isArray(ratings.value) && ratings.value.length > 0) {
		ratingsUsed.value = ratings.value;
	}
	if (props.initial && Array.isArray(props.initial)) {
		props.initial.forEach(v => valueRows.value.push({ ...v }));
	}
});

</script>

<style lang="scss">
@import '../themes/generated/variables.base.scss';

fieldset.topic-validation-error {
	border-left: 5px solid #f44336;
}
div.topic-validation-error {
	margin-top: 5px;
	color: #f44336;
}
.rated-topics {
	.other-row {
		margin-top: 1em;
	}
	.rt-row {
		padding: 10px 20px;
		border: 1px solid $base-border-color;
		border-left: 15px solid $base-border-color;
		margin-bottom: 1em;
		.rt-level {
			background: $base-border-color;
			border: 1px solid $base-border-color;
			border-radius: 5px;
			padding: 2px 5px;
			margin-left: 10px;
		}
		.rt-remove {
			cursor: pointer;
			float: right;
			margin-top: -4px;
			color: #f44336;
		}
	}
}
</style>